import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Save, Info, Layers } from "lucide-react";
import { toast } from "sonner";

// Color steps 0..4 (light → dark) per la matrice
const SCORE_BG = ["bg-slate-50", "bg-amber-50", "bg-amber-100", "bg-viva-100", "bg-viva-200"];
const SCORE_RING = ["ring-slate-200", "ring-amber-200", "ring-amber-300", "ring-viva-300", "ring-viva-500"];
const SCORE_LABEL = ["Non rilevante", "Bassa", "Media", "Alta", "Massima"];

function pillarColor(code) {
  if (code.startsWith("E")) return { bg: "bg-viva-50", text: "text-viva-700", dot: "bg-viva-500" };
  if (code.startsWith("S")) return { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" };
  return { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500" };
}

export default function Materiality() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assessments/${id}/materiality`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  function setVal(code, field, val) {
    setRows((rs) => rs.map((r) => r.topic_code === code
      ? { ...r, [field]: val, relevance: Math.max(field === "impact" ? val : r.impact, field === "financial" ? val : r.financial) }
      : r
    ));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/assessments/${id}/materiality`, {
        topics: rows.map((r) => ({
          topic_code: r.topic_code,
          impact: r.impact,
          financial: r.financial,
          motivation: r.motivation || "",
        })),
      });
      toast.success("Doppia materialità salvata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  const stats = useMemo(() => {
    const total = rows.length;
    const filled = rows.filter((r) => r.impact > 0 || r.financial > 0).length;
    const materiali = rows.filter((r) => r.relevance >= 3).length;
    return { total, filled, materiali };
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="materiality-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-7 h-7 text-viva-600" />
            Analisi di Doppia Materialità
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Valuta ciascun tema ESG su due dimensioni: l'<span className="font-semibold text-slate-700">impatto</span> che l'azienda
            genera verso l'esterno e la <span className="font-semibold text-slate-700">rilevanza finanziaria</span> che il tema ha
            per i risultati aziendali. Scala 0-4 (0 = non rilevante, 4 = massima rilevanza).
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <div className="text-sm">
            <span className="text-slate-500">Compilati: </span>
            <span className="font-heading font-bold text-slate-900">{stats.filled}/{stats.total}</span>
            <span className="text-slate-400 mx-2">·</span>
            <span className="text-slate-500">Materiali (≥3): </span>
            <span className="font-heading font-bold text-viva-700">{stats.materiali}</span>
          </div>
          <Button onClick={save} disabled={saving} className="bg-viva-600 hover:bg-viva-700" data-testid="save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Salva
          </Button>
        </div>
      </header>

      {/* Legend */}
      <Card className="p-4 rounded-2xl border-slate-100 shadow-soft bg-white">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-viva-600 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-800">Materialità d'impatto:</strong> in che misura le attività dell'organizzazione
            generano impatti (positivi o negativi) sulle persone e sull'ambiente. <br />
            <strong className="text-slate-800">Materialità finanziaria:</strong> in che misura il tema ESG influenza i flussi
            di cassa, l'accesso a finanziamenti, i costi operativi e gli asset dell'organizzazione. <br />
            La <strong>rilevanza complessiva</strong> = massimo tra le due. Un tema con rilevanza ≥ 3 è considerato "materiale".
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="materiality-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 font-semibold text-slate-700 w-14">Topic</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Descrizione</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-700 w-44">Materialità d'impatto</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-700 w-44">Materialità finanziaria</th>
                <th className="text-center px-4 py-3 font-semibold text-slate-700 w-24">Rilevanza</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Motivazione</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = pillarColor(r.topic_code);
                const rel = r.relevance;
                return (
                  <tr key={r.topic_code} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`mat-row-${r.topic_code}`}>
                    <td className="px-4 py-3 align-middle">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${c.bg} ${c.text} font-bold text-xs`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                        {r.topic_code}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-middle text-slate-700">{r.label}</td>
                    <td className="px-2 py-3 align-middle">
                      <ScoreSelector value={r.impact} onChange={(v) => setVal(r.topic_code, "impact", v)} testid={`impact-${r.topic_code}`} />
                    </td>
                    <td className="px-2 py-3 align-middle">
                      <ScoreSelector value={r.financial} onChange={(v) => setVal(r.topic_code, "financial", v)} testid={`fin-${r.topic_code}`} />
                    </td>
                    <td className="px-2 py-3 text-center align-middle">
                      <span
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg font-heading font-bold text-sm ring-2 ${SCORE_BG[rel]} ${SCORE_RING[rel]} ${rel >= 3 ? "text-viva-800" : rel >= 1 ? "text-amber-700" : "text-slate-400"}`}
                        data-testid={`rel-${r.topic_code}`}
                        title={SCORE_LABEL[rel]}
                      >
                        {rel}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-viva-500 focus:border-transparent"
                        placeholder="Breve motivazione del giudizio…"
                        value={r.motivation || ""}
                        onChange={(e) => setVal(r.topic_code, "motivation", e.target.value)}
                        maxLength={1000}
                        data-testid={`motiv-${r.topic_code}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Matrix visualization */}
      <MaterialityMatrix rows={rows} />

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="bg-viva-600 hover:bg-viva-700" data-testid="save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Salva valutazione
        </Button>
      </div>
    </div>
  );
}

function ScoreSelector({ value, onChange, testid }) {
  return (
    <div className="flex justify-center gap-1" data-testid={testid}>
      {[0, 1, 2, 3, 4].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`Valore ${n}`}
          title={SCORE_LABEL[n]}
          onClick={() => onChange(n)}
          className={`w-7 h-7 rounded-md text-xs font-bold transition-all ring-1
            ${value === n
              ? `${SCORE_BG[n]} ring-2 ring-viva-500 text-viva-800 shadow-soft scale-110`
              : "bg-white ring-slate-200 text-slate-500 hover:ring-viva-300 hover:bg-slate-50"}`}
          data-testid={`${testid}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function MaterialityMatrix({ rows }) {
  // Matrice 5x5: X = impact, Y = financial. Per ciascuna cella, mostra
  // i topic_code che hanno quella combinazione (handle clashing).
  const cells = {};
  for (const r of rows) {
    const k = `${r.impact}_${r.financial}`;
    (cells[k] = cells[k] || []).push(r.topic_code);
  }

  return (
    <Card className="rounded-2xl border-slate-100 shadow-soft p-6" data-testid="materiality-matrix">
      <h3 className="font-heading font-semibold text-slate-900 mb-1">Matrice di Materialità</h3>
      <p className="text-xs text-slate-500 mb-5">
        Visualizzazione dei 21 topic ESG sulla matrice impatto × finanziaria. La zona in alto a destra (entrambi ≥ 3) identifica i temi <span className="font-semibold text-viva-700">strategicamente prioritari</span>.
      </p>
      <div className="flex items-stretch">
        {/* Y axis label */}
        <div className="flex items-center justify-center pr-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold -rotate-90 whitespace-nowrap origin-center">
            Materialità finanziaria →
          </div>
        </div>
        {/* Grid */}
        <div className="flex-1">
          <div className="grid grid-cols-6 gap-1.5">
            {/* Y axis labels + cells */}
            {[4, 3, 2, 1, 0].map((y) => (
              <div key={`row-${y}`} className="contents">
                <div className="flex items-center justify-end pr-2 text-xs font-bold text-slate-400">{y}</div>
                {[0, 1, 2, 3, 4].map((x) => {
                  const k = `${x}_${y}`;
                  const codes = cells[k] || [];
                  const isMat = x >= 3 && y >= 3;
                  const intensity = Math.max(x, y);
                  return (
                    <div
                      key={k}
                      className={`relative h-16 rounded-lg ${SCORE_BG[intensity]} ring-1 ${isMat ? "ring-2 ring-viva-500" : "ring-slate-200"} p-1.5 flex flex-wrap gap-1 items-start content-start overflow-hidden`}
                      data-testid={`matrix-cell-${x}-${y}`}
                    >
                      {codes.map((c) => {
                        const cc = pillarColor(c);
                        return (
                          <span key={c} className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cc.bg} ${cc.text} ring-1 ring-white shadow-sm`}>
                            {c}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
            {/* X axis */}
            <div />
            {[0, 1, 2, 3, 4].map((x) => (
              <div key={`xl-${x}`} className="text-center text-xs font-bold text-slate-400 pt-1">{x}</div>
            ))}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-center mt-3">Materialità d'impatto →</div>
        </div>
      </div>
      <div className="flex gap-4 mt-5 text-xs text-slate-600 flex-wrap">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-viva-500" /> Pillar E</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500" /> Pillar S</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-indigo-500" /> Pillar G</span>
        <span className="inline-flex items-center gap-1.5 ml-auto"><span className="w-3 h-3 rounded ring-2 ring-viva-500" /> Zona materiale (≥3,≥3)</span>
      </div>
    </Card>
  );
}
